import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { exhaustMap, map, catchError, switchMap } from "rxjs/operators";
import { getIpDataState } from "@nxplatform/geo-data/slices/ip-data.slice";
import { whenPageVisible } from "@nxplatform/helpers/common";
import { getCustomerTokenSuccess, getCustomerTokenStart, getCustomerTokenFailure, logoutCustomer } from "@nxplatform/store-shared/customer/shared-customer-actions";
import { CUSTOMER_TOKEN_FEATURE_KEY } from "@nxplatform/store-shared/customer/shared-customer-feature-keys";
import { getCustomerTokenState } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
import { customerAuthData, customerAuthTokenName, tokenAction } from "@service/api-client/token/token-action";
const notificationStorageKey = "customerInfo";
const initialCustomerTokenState = {
  entities: AppStorage.get(customerAuthTokenName),
  loaded: !!AppStorage.get(customerAuthTokenName),
  error: null,
  errorMessage: null,
  errorMessageParams: null,
  customerData: AppStorage.get(customerAuthData),
  logoutInfo: AppStorage.get(notificationStorageKey) || null,
  jwtSuccess: false
};
var CustomCustomerEvents = /* @__PURE__ */ ((CustomCustomerEvents2) => {
  CustomCustomerEvents2["login"] = "customerLogin";
  CustomCustomerEvents2["logout"] = "customerLogout";
  return CustomCustomerEvents2;
})(CustomCustomerEvents || {});
const customerTokenSlice = createSlice({
  name: CUSTOMER_TOKEN_FEATURE_KEY,
  initialState: initialCustomerTokenState,
  reducers: {
    messageError: (state, action) => {
      state.errorMessage = action.payload.message;
      state.errorMessageParams = action.payload.params;
    },
    customerNotifyStart: () => {
    },
    customerNotifySuccess: (state, action) => {
      var _a;
      state.logoutInfo = (_a = action.payload) == null ? void 0 : _a.notification;
    },
    customerNotifyFailure: () => {
    },
    updateCustomerLimitsInfo: (state, action) => {
      state.logoutInfo = action.payload;
    },
    clearCustomerNotificationState: (state) => {
      state.logoutInfo = null;
    },
    markCustomerActivityStart: () => {
    },
    markCustomerActivitySuccess: () => {
    },
    markCustomerActivityError: () => {
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomerTokenSuccess, (state, action) => {
      state.loaded = true;
      tokenAction.add(action.payload);
      document.dispatchEvent(new Event(
        "customerLogin"
        /* login */
      ));
      state.customerData = tokenAction.getData().customerData;
      state.entities = action.payload;
      state.jwtSuccess = true;
    });
    builder.addCase(getCustomerTokenStart, (state, action) => {
      state.loaded = false;
    });
    builder.addCase(getCustomerTokenFailure, (state, action) => {
      state.error = action.payload;
      state.entities = null;
      state.loaded = false;
      state.customerData = initialCustomerTokenState.customerData;
      tokenAction.remove();
    });
    builder.addCase(logoutCustomer, (state, action) => {
      state.entities = null;
      state.loaded = false;
      document.dispatchEvent(new Event(
        "customerLogout"
        /* logout */
      ));
      state.customerData = initialCustomerTokenState.customerData;
      tokenAction.remove();
    });
  }
});
const customerTokenReducer = customerTokenSlice.reducer;
const {
  messageError,
  customerNotifyStart,
  customerNotifySuccess,
  customerNotifyFailure,
  updateCustomerLimitsInfo,
  clearCustomerNotificationState,
  markCustomerActivitySuccess,
  markCustomerActivityStart,
  markCustomerActivityError
} = customerTokenSlice.actions;
const selectIsNewRegistration = createSelector(
  [getCustomerTokenState, getIpDataState],
  (s, c) => {
    var _a;
    return !!(c.registrationSuccess || !((_a = s.entities) == null ? void 0 : _a.token));
  }
);
const selectCustomerTokenLoaded = createSelector(
  getCustomerTokenState,
  (s) => s.loaded
);
const selectJwtSuccess = createSelector(
  getCustomerTokenState,
  (s) => s.jwtSuccess
);
const selectCustomerTokenData = createSelector(
  getCustomerTokenState,
  (s) => s.customerData
);
const selectCustomerLogoutInfo = createSelector(
  getCustomerTokenState,
  (s) => s.logoutInfo
);
const selectCustomerTokenError = createSelector(
  getCustomerTokenState,
  (s) => s.error
);
const selectErrorMessage = createSelector(
  getCustomerTokenState,
  (s) => s.errorMessage
);
const selectErrorMessageParams = createSelector(
  getCustomerTokenState,
  (s) => s.errorMessageParams
);
const tokenRefresh = (token) => ({
  type: getCustomerTokenStart.type,
  payload: token
});
const fetchRefreshTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCustomerTokenStart.type),
  exhaustMap((action) => Api.customerNoAuth.tokenRefresh(action.payload.token).pipe(
    map((data) => getCustomerTokenSuccess(data)),
    catchError((err) => of(getCustomerTokenFailure(err)))
  ))
);
const customerLogoutNotify = () => ({ type: customerNotifyStart.type });
const fetchCustomerNotify = (action$, state$, { Api }) => action$.pipe(
  ofType(customerNotifyStart.type),
  exhaustMap(() => Api.customerNoAuth.logoutNotify().pipe(
    map((data) => customerNotifySuccess(data)),
    catchError(() => of(customerNotifyFailure()))
  ))
);
const markCustomerActivity = () => ({
  type: markCustomerActivityStart.type
});
const markCustomerActivityEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(markCustomerActivityStart.type),
  whenPageVisible(),
  switchMap(() => Api.customerNoAuth.markCustomerActivity().pipe(
    map(() => markCustomerActivitySuccess()),
    catchError(() => of(markCustomerActivityError()))
  ))
);
export {
  CustomCustomerEvents,
  clearCustomerNotificationState,
  customerLogoutNotify,
  customerNotifyFailure,
  customerNotifyStart,
  customerNotifySuccess,
  customerTokenReducer,
  customerTokenSlice,
  fetchCustomerNotify,
  fetchRefreshTokenEpic,
  initialCustomerTokenState,
  markCustomerActivity,
  markCustomerActivityEpic,
  markCustomerActivityError,
  markCustomerActivityStart,
  markCustomerActivitySuccess,
  messageError,
  notificationStorageKey,
  selectCustomerLogoutInfo,
  selectCustomerTokenData,
  selectCustomerTokenError,
  selectCustomerTokenLoaded,
  selectErrorMessage,
  selectErrorMessageParams,
  selectIsNewRegistration,
  selectJwtSuccess,
  tokenRefresh,
  updateCustomerLimitsInfo
};
